import React, { useState } from 'react';
import '../Styles/ToolBox.css';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { PDFDocument } from 'pdf-lib';
import { removeBackground } from '@imgly/background-removal';
import imageCompression from 'browser-image-compression';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import SignatureCanvas from 'react-signature-canvas';

function ToolBox() {
  const [files, setFiles] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Background Remover states
  const [bgRemoverReady, setBgRemoverReady] = useState(false);
  const [bgRemoverLoading, setBgRemoverLoading] = useState(false);
  const [bgRemoverProgress, setBgRemoverProgress] = useState(0);
  const [bgOriginalImage, setBgOriginalImage] = useState(null);
  const [bgProcessedImage, setBgProcessedImage] = useState(null);
  const [bgProcessing, setBgProcessing] = useState(false);

  // Image Size Reducer states
  const [originalImage, setOriginalImage] = useState(null);
  const [originalImageFile, setOriginalImageFile] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null);
  const [compressedImageFile, setCompressedImageFile] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [compressionMode, setCompressionMode] = useState('target'); // 'target' or 'quality'
  const [selectedTarget, setSelectedTarget] = useState(1); // 0.5, 1, 2, 4 MB

  // QR Code Generator states
  const [qrText, setQrText] = useState('');
  const [qrSize, setQrSize] = useState(256);

  // Barcode Generator states
  const [barcodeValue, setBarcodeValue] = useState('');
  const [barcodeFormat, setBarcodeFormat] = useState('CODE128');

  // Signature Maker states
  const [signatureRef, setSignatureRef] = useState(null);

  // Unit Converter states
  const [weightKg, setWeightKg] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [heightM, setHeightM] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [tempC, setTempC] = useState('');
  const [tempF, setTempF] = useState('');
  const [lastEditedWeight, setLastEditedWeight] = useState(''); // 'kg' or 'lbs'
  const [lastEditedHeight, setLastEditedHeight] = useState(''); // 'm' or 'ft'
  const [lastEditedTemp, setLastEditedTemp] = useState(''); // 'c' or 'f'

  // Currency Converter states
  const [currencyFrom, setCurrencyFrom] = useState('USD');
  const [currencyTo, setCurrencyTo] = useState('INR');
  const [amountFrom, setAmountFrom] = useState('');
  const [amountTo, setAmountTo] = useState('');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastEditedCurrency, setLastEditedCurrency] = useState(''); // 'from' or 'to'
  const [loadingRate, setLoadingRate] = useState(false);

  // Fetch exchange rate on mount and when currencies change
  React.useEffect(() => {
    fetchExchangeRate();
  }, [currencyFrom, currencyTo]);

  // Recalculate amounts when exchange rate changes
  React.useEffect(() => {
    if (exchangeRate && lastEditedCurrency === 'from' && amountFrom !== '') {
      const converted = (parseFloat(amountFrom) * exchangeRate).toFixed(2);
      setAmountTo(converted);
    } else if (exchangeRate && lastEditedCurrency === 'to' && amountTo !== '') {
      const converted = (parseFloat(amountTo) / exchangeRate).toFixed(2);
      setAmountFrom(converted);
    }
  }, [exchangeRate]);

  const fetchExchangeRate = async () => {
    setLoadingRate(true);
    try {
      // Using exchangerate-api.com - supports more currencies including AZN, UZS, OMR, LKR
      const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${currencyFrom}`);
      const data = await response.json();
      
      if (data.rates && data.rates[currencyTo]) {
        setExchangeRate(data.rates[currencyTo]);
        setLastUpdated(new Date());
      } else {
        console.error('Currency not supported:', currencyTo);
        alert(`Currency ${currencyTo} not supported. Please try another.`);
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      alert('Failed to fetch exchange rate. Please try again.');
    } finally {
      setLoadingRate(false);
    }
  };

  // Get currency symbol
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
      'AED': 'د.إ',
      'JPY': '¥',
      'CNY': '¥',
      'AUD': 'A$',
      'CAD': 'C$',
      'IDR': 'Rp',
      'THB': '฿',
      'KZT': '₸',
      'SGD': 'S$',
      'RUB': '₽',
      'TRY': '₺',
      'AZN': '₼',
      'UZS': 'soʻm',
      'OMR': 'ر.ع.',
      'VND': '₫',
      'LKR': 'Rs'
    };
    return symbols[currency] || currency;
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files);
    addFiles(uploadedFiles);
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Add files to state
  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(file => {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      return validTypes.includes(file.type);
    });

    const filesWithPreview = validFiles.map((file, index) => ({
      id: Date.now() + index,
      file: file,
      preview: URL.createObjectURL(file),
      name: file.name,
      type: file.type
    }));

    setFiles(prev => [...prev, ...filesWithPreview]);
  };

  // Delete file
  const deleteFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // Drag to reorder
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFiles = [...files];
    const draggedFile = newFiles[draggedIndex];
    newFiles.splice(draggedIndex, 1);
    newFiles.splice(index, 0, draggedFile);
    
    setDraggedIndex(index);
    setFiles(newFiles);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Convert image to PDF
  const imageToPdf = async (imageFile) => {
    const pdfDoc = await PDFDocument.create();
    const imageBytes = await imageFile.arrayBuffer();
    
    let image;
    if (imageFile.type === 'image/png') {
      image = await pdfDoc.embedPng(imageBytes);
    } else {
      image = await pdfDoc.embedJpg(imageBytes);
    }
    
    const page = pdfDoc.addPage([image.width, image.height]);
    page.drawImage(image, {
      x: 0,
      y: 0,
      width: image.width,
      height: image.height,
    });
    
    return pdfDoc;
  };

  // Merge and download PDFs
  const handleMerge = async () => {
    if (files.length === 0) {
      alert('Please upload at least one file');
      return;
    }

    try {
      const mergedPdf = await PDFDocument.create();

      for (const fileData of files) {
        if (fileData.type === 'application/pdf') {
          // Handle PDF
          const pdfBytes = await fileData.file.arrayBuffer();
          const pdf = await PDFDocument.load(pdfBytes);
          const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          copiedPages.forEach((page) => mergedPdf.addPage(page));
        } else {
          // Handle images (PNG/JPEG)
          const imagePdf = await imageToPdf(fileData.file);
          const copiedPages = await mergedPdf.copyPages(imagePdf, imagePdf.getPageIndices());
          copiedPages.forEach((page) => mergedPdf.addPage(page));
        }
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'merged-document.pdf';
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error merging PDFs:', error);
      alert('Error merging files. Please try again.');
    }
  };

  // Unit Converter - Weight
  const handleWeightKgChange = (value) => {
    setWeightKg(value);
    setLastEditedWeight('kg');
    if (value === '') {
      setWeightLbs('');
    } else {
      const lbs = (parseFloat(value) * 2.20462).toFixed(2);
      setWeightLbs(lbs);
    }
  };

  const handleWeightLbsChange = (value) => {
    setWeightLbs(value);
    setLastEditedWeight('lbs');
    if (value === '') {
      setWeightKg('');
    } else {
      const kg = (parseFloat(value) / 2.20462).toFixed(2);
      setWeightKg(kg);
    }
  };

  // Unit Converter - Height
  const handleHeightMChange = (value) => {
    setHeightM(value);
    setLastEditedHeight('m');
    if (value === '') {
      setHeightFt('');
      setHeightIn('');
    } else {
      const totalInches = parseFloat(value) * 39.3701;
      const ft = Math.floor(totalInches / 12);
      const inches = (totalInches % 12).toFixed(1);
      setHeightFt(ft.toString());
      setHeightIn(inches);
    }
  };

  const handleHeightFtInChange = (ft, inches) => {
    setHeightFt(ft);
    setHeightIn(inches);
    setLastEditedHeight('ft');
    if (ft === '' && inches === '') {
      setHeightM('');
    } else {
      const totalInches = (parseFloat(ft) || 0) * 12 + (parseFloat(inches) || 0);
      const meters = (totalInches / 39.3701).toFixed(2);
      setHeightM(meters);
    }
  };

  // Unit Converter - Temperature
  const handleTempCChange = (value) => {
    setTempC(value);
    setLastEditedTemp('c');
    if (value === '') {
      setTempF('');
    } else {
      const f = ((parseFloat(value) * 9/5) + 32).toFixed(1);
      setTempF(f);
    }
  };

  const handleTempFChange = (value) => {
    setTempF(value);
    setLastEditedTemp('f');
    if (value === '') {
      setTempC('');
    } else {
      const c = ((parseFloat(value) - 32) * 5/9).toFixed(1);
      setTempC(c);
    }
  };

  // Currency Converter
  const handleAmountFromChange = (value) => {
    setAmountFrom(value);
    setLastEditedCurrency('from');
    if (value === '' || !exchangeRate) {
      setAmountTo('');
    } else {
      const converted = (parseFloat(value) * exchangeRate).toFixed(2);
      setAmountTo(converted);
    }
  };

  const handleAmountToChange = (value) => {
    setAmountTo(value);
    setLastEditedCurrency('to');
    if (value === '' || !exchangeRate) {
      setAmountFrom('');
    } else {
      const converted = (parseFloat(value) / exchangeRate).toFixed(2);
      setAmountFrom(converted);
    }
  };

  const getTimeSinceUpdate = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000 / 60); // minutes
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} min ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Background Remover - Initialize tool
  const initializeBackgroundRemover = async () => {
    setBgRemoverLoading(true);
    setBgRemoverProgress(0);
    
    try {
      // Simulate progress (actual progress tracking is complex)
      const progressInterval = setInterval(() => {
        setBgRemoverProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 500);

      // Preload the model by doing a tiny test removal
      const testCanvas = document.createElement('canvas');
      testCanvas.width = 10;
      testCanvas.height = 10;
      const testCtx = testCanvas.getContext('2d');
      testCtx.fillStyle = 'white';
      testCtx.fillRect(0, 0, 10, 10);
      
      await removeBackground(testCanvas.toDataURL());
      
      clearInterval(progressInterval);
      setBgRemoverProgress(100);
      
      setTimeout(() => {
        setBgRemoverReady(true);
        setBgRemoverLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error initializing background remover:', error);
      alert('Failed to initialize. Please try again.');
      setBgRemoverLoading(false);
    }
  };

  // Background Remover - Upload image
  const handleBgImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setBgOriginalImage(event.target.result);
        setBgProcessedImage(null);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload a PNG or JPEG image');
    }
  };

  // Background Remover - Process image
  const handleRemoveBackground = async () => {
    if (!bgOriginalImage) {
      alert('Please upload an image first');
      return;
    }

    setBgProcessing(true);
    
    try {
      const blob = await removeBackground(bgOriginalImage);
      const url = URL.createObjectURL(blob);
      setBgProcessedImage(url);
    } catch (error) {
      console.error('Error removing background:', error);
      alert('Failed to remove background. Please try again.');
    } finally {
      setBgProcessing(false);
    }
  };

  // Background Remover - Download processed image
  const handleDownloadBgImage = () => {
    if (!bgProcessedImage) return;
    
    const link = document.createElement('a');
    link.href = bgProcessedImage;
    link.download = 'background-removed.png';
    link.click();
  };

  // Background Remover - Reset
  const handleResetBgRemover = () => {
    setBgOriginalImage(null);
    setBgProcessedImage(null);
  };

  // Image Size Reducer - Upload image
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg')) {
      setOriginalImageFile(file);
      setOriginalImage(URL.createObjectURL(file));
      setCompressedImage(null);
      setCompressedImageFile(null);
    } else {
      alert('Please upload a PNG or JPEG image');
    }
  };

  // Image Size Reducer - Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Image Size Reducer - Get image dimensions
  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Image Size Reducer - Compress by target size
  const compressByTarget = async (targetMB) => {
    if (!originalImageFile) {
      alert('Please upload an image first');
      return;
    }

    setCompressing(true);
    setSelectedTarget(targetMB);

    try {
      const options = {
        maxSizeMB: targetMB,
        maxWidthOrHeight: 1920,
        maxIteration: 20,
        useWebWorker: true
      };

      const compressed = await imageCompression(originalImageFile, options);
      setCompressedImageFile(compressed);
      setCompressedImage(URL.createObjectURL(compressed));
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Failed to compress image. Please try again.');
    } finally {
      setCompressing(false);
    }
  };

  // Image Size Reducer - Compress by quality preset
  const compressByQuality = async (preset) => {
    if (!originalImageFile) {
      alert('Please upload an image first');
      return;
    }

    setCompressing(true);

    try {
      let options;
      
      switch(preset) {
        case 'light':
          options = {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            maxIteration: 15,
            initialQuality: 0.8,
            useWebWorker: true
          };
          break;
        case 'medium':
          options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1600,
            maxIteration: 20,
            initialQuality: 0.7,
            useWebWorker: true
          };
          break;
        case 'heavy':
          options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1200,
            maxIteration: 25,
            initialQuality: 0.6,
            useWebWorker: true
          };
          break;
        case 'maximum':
          options = {
            maxSizeMB: 0.3,
            maxWidthOrHeight: 1000,
            maxIteration: 30,
            initialQuality: 0.5,
            useWebWorker: true
          };
          break;
        default:
          options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            maxIteration: 20,
            useWebWorker: true
          };
      }

      const compressed = await imageCompression(originalImageFile, options);
      setCompressedImageFile(compressed);
      setCompressedImage(URL.createObjectURL(compressed));
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Failed to compress image. Please try again.');
    } finally {
      setCompressing(false);
    }
  };

  // Image Size Reducer - Download compressed image
  const handleDownloadCompressed = () => {
    if (!compressedImageFile) return;
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(compressedImageFile);
    link.download = `compressed-${originalImageFile.name}`;
    link.click();
  };

  // Image Size Reducer - Reset
  const handleResetImageReducer = () => {
    setOriginalImage(null);
    setOriginalImageFile(null);
    setCompressedImage(null);
    setCompressedImageFile(null);
  };

  // Image Size Reducer - Calculate reduction percentage
  const getReductionPercentage = () => {
    if (!originalImageFile || !compressedImageFile) return 0;
    const reduction = ((originalImageFile.size - compressedImageFile.size) / originalImageFile.size * 100);
    return Math.round(reduction);
  };

  // QR Code Generator - Download QR Code
  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = 'qr-code.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Barcode Generator - Download Barcode
  const downloadBarcode = () => {
    const svg = document.querySelector('.Barcode-display svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = 'barcode.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Signature Maker - Clear signature
  const clearSignature = () => {
    if (signatureRef) {
      signatureRef.clear();
    }
  };

  // Signature Maker - Download signature
  const downloadSignature = () => {
    if (signatureRef && !signatureRef.isEmpty()) {
      const dataURL = signatureRef.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = 'signature.png';
      downloadLink.href = dataURL;
      downloadLink.click();
    } else {
      alert('Please draw a signature first');
    }
  };

  return (
    <div className='ToolBox-container'>
      <h1 className='ToolBox-title'>TOOLBOX</h1>

      {/* Converter Widgets */}
      <div className='ToolBox-converters'>
        {/* Unit Converter */}
        <div className='Converter-widget Converter-widget1'>
          {/* Weight */}
          <div className='Converter-row'>
            <label className='Converter-label Converter-labelTop'>Weight</label>
            <div className='Converter-inputs'>
              <div className={`Converter-input-group ${lastEditedWeight === 'lbs' ? 'highlighted' : ''}`}>
                <input 
                  type='number'
                  className='Converter-input'
                  value={weightKg}
                  onChange={(e) => handleWeightKgChange(e.target.value)}
                  placeholder='0'
                />
                <span className='Converter-unit'>kg</span>
              </div>
              <div className={`Converter-input-group ${lastEditedWeight === 'kg' ? 'highlighted' : ''}`}>
                <input 
                  type='number'
                  className='Converter-input'
                  value={weightLbs}
                  onChange={(e) => handleWeightLbsChange(e.target.value)}
                  placeholder='0'
                />
                <span className='Converter-unit'>lbs</span>
              </div>
            </div>
          </div>

          {/* Height */}
          <div className='Converter-row'>
            <label className='Converter-label'>Height</label>
            <div className='Converter-inputs'>
              <div className={`Converter-input-group ${lastEditedHeight === 'ft' ? 'highlighted' : ''}`}>
                <input 
                  type='number'
                  className='Converter-input'
                  value={heightM}
                  onChange={(e) => handleHeightMChange(e.target.value)}
                  placeholder='0'
                />
                <span className='Converter-unit'>m</span>
              </div>
              <div className={`Converter-input-group height-ft ${lastEditedHeight === 'm' ? 'highlighted' : ''}`}>
                <input 
                  type='number'
                  className='Converter-input small'
                  value={heightFt}
                  onChange={(e) => handleHeightFtInChange(e.target.value, heightIn)}
                  placeholder='0'
                />
                <span className='Converter-unit'>ft</span>
                <input 
                  type='number'
                  className='Converter-input small'
                  value={heightIn}
                  onChange={(e) => handleHeightFtInChange(heightFt, e.target.value)}
                  placeholder='0'
                />
                <span className='Converter-unit Converter-unitInch'>in</span>
              </div>
            </div>
          </div>

          {/* Temperature */}
          <div className='Converter-row'>
            <label className='Converter-label Converter-labelBottom'>Temperature</label>
            <div className='Converter-inputs'>
              <div className={`Converter-input-group ${lastEditedTemp === 'f' ? 'highlighted' : ''}`}>
                <input 
                  type='number'
                  className='Converter-input'
                  value={tempC}
                  onChange={(e) => handleTempCChange(e.target.value)}
                  placeholder='0'
                />
                <span className='Converter-unit'>°C</span>
              </div>
              <div className={`Converter-input-group ${lastEditedTemp === 'c' ? 'highlighted' : ''}`}>
                <input 
                  type='number'
                  className='Converter-input'
                  value={tempF}
                  onChange={(e) => handleTempFChange(e.target.value)}
                  placeholder='0'
                />
                <span className='Converter-unit'>°F</span>
              </div>
            </div>
          </div>
        </div>

        {/* Currency Converter */}
        <div className='Converter-widget'>
          <div className='Converter-row currency'>
            <div className='Converter-currency-selectors'>
              <select 
                className='Converter-currency-select'
                value={currencyFrom}
                onChange={(e) => setCurrencyFrom(e.target.value)}
              >
                <option value='USD'>USD - USA</option>
                <option value='EUR'>EUR - Eurozone</option>
                <option value='GBP'>GBP - UK</option>
                <option value='INR'>INR - India</option>
                <option value='AED'>AED - UAE</option>
                <option value='JPY'>JPY - Japan</option>
                <option value='CNY'>CNY - China</option>
                <option value='AUD'>AUD - Australia</option>
                <option value='CAD'>CAD - Canada</option>
                <option value='IDR'>IDR - Indonesia</option>
                <option value='THB'>THB - Thailand</option>
                <option value='KZT'>KZT - Kazakhstan</option>
                <option value='SGD'>SGD - Singapore</option>
                <option value='RUB'>RUB - Russia</option>
                <option value='TRY'>TRY - Turkey</option>
                <option value='AZN'>AZN - Azerbaijan</option>
                <option value='UZS'>UZS - Uzbekistan</option>
                <option value='OMR'>OMR - Oman</option>
                <option value='VND'>VND - Vietnam</option>
                <option value='LKR'>LKR - Sri Lanka</option>
              </select>
              <select 
                className='Converter-currency-select'
                value={currencyTo}
                onChange={(e) => setCurrencyTo(e.target.value)}
              >
                <option value='INR'>INR - India</option>
                <option value='USD'>USD - USA</option>
                <option value='EUR'>EUR - Eurozone</option>
                <option value='GBP'>GBP - UK</option>
                <option value='AED'>AED - UAE</option>
                <option value='JPY'>JPY - Japan</option>
                <option value='CNY'>CNY - China</option>
                <option value='AUD'>AUD - Australia</option>
                <option value='CAD'>CAD - Canada</option>
                <option value='IDR'>IDR - Indonesia</option>
                <option value='THB'>THB - Thailand</option>
                <option value='KZT'>KZT - Kazakhstan</option>
                <option value='SGD'>SGD - Singapore</option>
                <option value='RUB'>RUB - Russia</option>
                <option value='TRY'>TRY - Turkey</option>
                <option value='AZN'>AZN - Azerbaijan</option>
                <option value='UZS'>UZS - Uzbekistan</option>
                <option value='OMR'>OMR - Oman</option>
                <option value='VND'>VND - Vietnam</option>
                <option value='LKR'>LKR - Sri Lanka</option>
              </select>
            </div>
          </div>

          <div className='Converter-row'>
            <div className='Converter-inputs currency-inputs'>
              <div className={`Converter-input-group ${lastEditedCurrency === 'to' ? 'highlighted' : ''}`}>
                <span className='Converter-currency-symbol'>{getCurrencySymbol(currencyFrom)}</span>
                <input 
                  type='number'
                  className='Converter-input'
                  value={amountFrom}
                  onChange={(e) => handleAmountFromChange(e.target.value)}
                  placeholder='0'
                  disabled={loadingRate}
                />
              </div>
              <div className={`Converter-input-group ${lastEditedCurrency === 'from' ? 'highlighted' : ''}`}>
                <span className='Converter-currency-symbol'>{getCurrencySymbol(currencyTo)}</span>
                <input 
                  type='number'
                  className='Converter-input'
                  value={amountTo}
                  onChange={(e) => handleAmountToChange(e.target.value)}
                  placeholder='0'
                  disabled={loadingRate}
                />
              </div>
            </div>
          </div>

          <div className='Converter-update-info'>
            {loadingRate ? (
              <p>Loading rate...</p>
            ) : exchangeRate ? (
              <p>Updated: {getTimeSinceUpdate()}</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className='ToolBox-accordions'>
        {/* PDF Merger Tool */}
        <Accordion className='ToolBox-accordion'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='ToolBox-accordion-summary'
          >
            <p className='ToolBox-accordion-title'>PDF Merger</p>
          </AccordionSummary>
          <AccordionDetails className='ToolBox-accordion-details'>
            <div className='PDFMerger-container'>
              {/* Main Area - Upload and Preview Side by Side */}
              <div className='PDFMerger-main-area'>
                {/* Upload Area */}
                <div 
                  className='PDFMerger-upload-area'
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <div className='PDFMerger-upload-icon'>↑</div>
                  <button 
                    className='PDFMerger-upload-button'
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    Upload Items
                  </button>
                  <input
                    id='file-input'
                    type='file'
                    multiple
                    accept='.pdf,.png,.jpg,.jpeg'
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <p className='PDFMerger-supported-formats'>Supported Formats: Png, JPEG, PDF</p>
                </div>

                {/* Preview Area */}
                <div className='PDFMerger-preview-area'>
                  {files.length > 0 ? (
                    files.map((fileData, index) => (
                      <div
                        key={fileData.id}
                        className='PDFMerger-thumbnail-wrapper'
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className='PDFMerger-thumbnail'>
                          <button 
                            className='PDFMerger-delete-button'
                            onClick={() => deleteFile(fileData.id)}
                          >
                            <CloseIcon style={{ fontSize: '1rem' }} />
                          </button>
                          {fileData.type === 'application/pdf' ? (
                            <div className='PDFMerger-pdf-icon'>PDF</div>
                          ) : (
                            <img 
                              src={fileData.preview} 
                              alt={fileData.name}
                              className='PDFMerger-thumbnail-image'
                            />
                          )}
                        </div>
                        <p className='PDFMerger-filename' title={fileData.name}>
                          {fileData.name}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className='PDFMerger-no-files'>We don't save or upload<br/>items added here</p>
                  )}
                </div>
              </div>

              {/* Merge Button */}
              <button 
                className='PDFMerger-merge-button'
                onClick={handleMerge}
                disabled={files.length === 0}
              >
                Merge & Download
              </button>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Image Size Reducer */}
        <Accordion className='ToolBox-accordion'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='ToolBox-accordion-summary'
          >
            <p className='ToolBox-accordion-title'>Image Size Reducer</p>
          </AccordionSummary>
          <AccordionDetails className='ToolBox-accordion-details'>
            <div className='ImageReducer-container'>
              {/* Mode Selector */}
              <div className='ImageReducer-mode-selector'>
                <button 
                  className={`ImageReducer-mode-button ${compressionMode === 'target' ? 'active' : ''}`}
                  onClick={() => setCompressionMode('target')}
                >
                  Target File Size
                </button>
                <button 
                  className={`ImageReducer-mode-button ${compressionMode === 'quality' ? 'active' : ''}`}
                  onClick={() => setCompressionMode('quality')}
                >
                  Quality Presets
                </button>
              </div>

              {/* Upload Section */}
              {!originalImage ? (
                <div className='ImageReducer-upload-area'>
                  <p className='ImageReducer-upload-text'>Upload Image to Compress</p>
                  <button 
                    className='ImageReducer-upload-button'
                    onClick={() => document.getElementById('image-reducer-input').click()}
                  >
                    Choose Image
                  </button>
                  <input
                    id='image-reducer-input'
                    type='file'
                    accept='image/png,image/jpeg,image/jpg'
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <p className='ImageReducer-supported'>PNG or JPEG</p>
                </div>
              ) : (
                <div className='ImageReducer-main-area'>
                  {/* Original Image Info */}
                  <div className='ImageReducer-section'>
                    <h3 className='ImageReducer-section-title'>Original Image</h3>
                    <div className='ImageReducer-image-container'>
                      <img 
                        src={originalImage} 
                        alt='Original'
                        className='ImageReducer-image'
                      />
                    </div>
                    <div className='ImageReducer-info'>
                      <p className='ImageReducer-info-label'>File Size:</p>
                      <p className='ImageReducer-info-value'>{formatFileSize(originalImageFile.size)}</p>
                    </div>
                  </div>

                  {/* Compressed Image Info */}
                  <div className='ImageReducer-section'>
                    <h3 className='ImageReducer-section-title'>Compressed Image</h3>
                    {compressedImage ? (
                      <>
                        <div className='ImageReducer-image-container'>
                          <img 
                            src={compressedImage} 
                            alt='Compressed'
                            className='ImageReducer-image'
                          />
                        </div>
                        <div className='ImageReducer-info'>
                          <p className='ImageReducer-info-label'>File Size:</p>
                          <p className='ImageReducer-info-value success'>
                            {formatFileSize(compressedImageFile.size)}
                          </p>
                        </div>
                        <div className='ImageReducer-info'>
                          <p className='ImageReducer-info-label'>Reduction:</p>
                          <p className='ImageReducer-info-value success'>
                            {getReductionPercentage()}% smaller
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className='ImageReducer-placeholder'>
                        {compressing ? (
                          <div className='ImageReducer-processing'>
                            <div className='ImageReducer-spinner'></div>
                            <p>Compressing image...</p>
                          </div>
                        ) : (
                          <p className='ImageReducer-placeholder-text'>
                            Select compression level below
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Compression Options */}
              {originalImage && !compressing && (
                <div className='ImageReducer-options'>
                  {compressionMode === 'target' ? (
                    <>
                      <p className='ImageReducer-options-title'>Target File Size:</p>
                      <div className='ImageReducer-buttons'>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByTarget(0.5)}
                        >
                          Under 500 KB
                        </button>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByTarget(1)}
                        >
                          Under 1 MB
                        </button>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByTarget(2)}
                        >
                          Under 2 MB
                        </button>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByTarget(4)}
                        >
                          Under 4 MB
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className='ImageReducer-options-title'>Quality Level:</p>
                      <div className='ImageReducer-buttons'>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByQuality('light')}
                        >
                          High (Web Quality)
                        </button>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByQuality('medium')}
                        >
                          Medium (Social Media)
                        </button>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByQuality('heavy')}
                        >
                          Low (Email)
                        </button>
                        <button 
                          className='ImageReducer-option-button'
                          onClick={() => compressByQuality('maximum')}
                        >
                          Very Low (Smallest)
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {originalImage && (
                <div className='ImageReducer-actions'>
                  <button 
                    className='ImageReducer-action-button primary'
                    onClick={handleDownloadCompressed}
                    disabled={!compressedImage}
                  >
                    Download Compressed Image
                  </button>
                  <button 
                    className='ImageReducer-action-button'
                    onClick={handleResetImageReducer}
                  >
                    Upload New Image
                  </button>
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Background Remover */}
        <Accordion className='ToolBox-accordion'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='ToolBox-accordion-summary'
          >
            <p className='ToolBox-accordion-title'>Background Remover</p>
          </AccordionSummary>
          <AccordionDetails className='ToolBox-accordion-details'>
            {!bgRemoverReady && !bgRemoverLoading ? (
              // Initial state - Show start button
              <div className='BackgroundRemover-init'>
                <p className='BackgroundRemover-init-text'>
                  This tool uses AI to remove backgrounds from images.
                  <br/>
                  <strong>First-time setup:</strong> Downloads ~50MB AI model (one-time, cached for future use)
                  <br/>
                  All processing happens in your browser - your images never leave your device.
                </p>
                <button 
                  className='BackgroundRemover-init-button'
                  onClick={initializeBackgroundRemover}
                >
                  Initialize Background Remover
                </button>
              </div>
            ) : bgRemoverLoading ? (
              // Loading state - Show progress
              <div className='BackgroundRemover-loading'>
                <p className='BackgroundRemover-loading-text'>Loading AI model...</p>
                <div className='BackgroundRemover-progress-bar'>
                  <div 
                    className='BackgroundRemover-progress-fill'
                    style={{ width: `${bgRemoverProgress}%` }}
                  ></div>
                </div>
                <p className='BackgroundRemover-progress-text'>{bgRemoverProgress}%</p>
                <p className='BackgroundRemover-loading-subtext'>
                  This may take 10-30 seconds depending on your internet speed
                </p>
              </div>
            ) : (
              // Ready state - Show tool
              <div className='BackgroundRemover-container'>
                <div className='BackgroundRemover-main-area'>
                  {/* Upload/Original Image */}
                  <div className='BackgroundRemover-section'>
                    <h3 className='BackgroundRemover-section-title'>Original Image</h3>
                    {bgOriginalImage ? (
                      <div className='BackgroundRemover-image-container'>
                        <img 
                          src={bgOriginalImage} 
                          alt='Original'
                          className='BackgroundRemover-image'
                        />
                      </div>
                    ) : (
                      <div className='BackgroundRemover-upload-area'>
                        <p className='BackgroundRemover-upload-text'>Upload Image</p>
                        <button 
                          className='BackgroundRemover-upload-button'
                          onClick={() => document.getElementById('bg-file-input').click()}
                        >
                          Choose File
                        </button>
                        <input
                          id='bg-file-input'
                          type='file'
                          accept='image/png,image/jpeg'
                          onChange={handleBgImageUpload}
                          style={{ display: 'none' }}
                        />
                        <p className='BackgroundRemover-supported'>PNG or JPEG</p>
                      </div>
                    )}
                  </div>

                  {/* Processed Image */}
                  <div className='BackgroundRemover-section'>
                    <h3 className='BackgroundRemover-section-title'>Processed Image</h3>
                    {bgProcessedImage ? (
                      <div className='BackgroundRemover-image-container'>
                        <img 
                          src={bgProcessedImage} 
                          alt='Processed'
                          className='BackgroundRemover-image'
                          style={{ 
                            background: 'repeating-conic-gradient(#ddd 0% 25%, white 0% 50%) 50% / 20px 20px' 
                          }}
                        />
                      </div>
                    ) : (
                      <div className='BackgroundRemover-placeholder'>
                        {bgProcessing ? (
                          <div className='BackgroundRemover-processing'>
                            <div className='BackgroundRemover-spinner'></div>
                            <p>Processing image...</p>
                            <p className='BackgroundRemover-processing-subtext'>This may take 3-10 seconds</p>
                          </div>
                        ) : (
                          <p className='BackgroundRemover-placeholder-text'>
                            Processed image will<br/>appear here
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='BackgroundRemover-actions'>
                  <button 
                    className='BackgroundRemover-action-button primary'
                    onClick={handleRemoveBackground}
                    disabled={!bgOriginalImage || bgProcessing}
                  >
                    {bgProcessing ? 'Processing...' : 'Remove Background'}
                  </button>
                  <button 
                    className='BackgroundRemover-action-button'
                    onClick={handleDownloadBgImage}
                    disabled={!bgProcessedImage}
                  >
                    Download Image
                  </button>
                  <button 
                    className='BackgroundRemover-action-button'
                    onClick={handleResetBgRemover}
                    disabled={!bgOriginalImage}
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </AccordionDetails>
        </Accordion>

        {/* QR Code Generator */}
        <Accordion className='ToolBox-accordion'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='ToolBox-accordion-summary'
          >
            <p className='ToolBox-accordion-title'>QR Code Generator</p>
          </AccordionSummary>
          <AccordionDetails className='ToolBox-accordion-details'>
            <div className='BusinessTool-container'>
              <div className='BusinessTool-input-section'>
                <label className='BusinessTool-label'>Enter Text or URL:</label>
                <input
                  type='text'
                  className='BusinessTool-input'
                  value={qrText}
                  onChange={(e) => setQrText(e.target.value)}
                  placeholder='https://www.thetrehan.com or any text...'
                />
                <div className='BusinessTool-size-control'>
                  <label className='BusinessTool-label-small'>Size: {qrSize}px</label>
                  <input
                    type='range'
                    min='128'
                    max='512'
                    value={qrSize}
                    onChange={(e) => setQrSize(parseInt(e.target.value))}
                    className='BusinessTool-slider'
                  />
                </div>
              </div>

              {qrText && (
                <div className='BusinessTool-output-section'>
                  <div className='BusinessTool-preview'>
                    <QRCodeSVG 
                      id='qr-code-svg'
                      value={qrText} 
                      size={qrSize}
                      level='H'
                      includeMargin={true}
                    />
                  </div>
                  <button 
                    className='BusinessTool-download-button'
                    onClick={downloadQRCode}
                  >
                    Download QR Code
                  </button>
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Barcode Generator */}
        <Accordion className='ToolBox-accordion'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='ToolBox-accordion-summary'
          >
            <p className='ToolBox-accordion-title'>Barcode Generator</p>
          </AccordionSummary>
          <AccordionDetails className='ToolBox-accordion-details'>
            <div className='BusinessTool-container'>
              <div className='BusinessTool-input-section'>
                <label className='BusinessTool-label'>Enter Number or Text:</label>
                <input
                  type='text'
                  className='BusinessTool-input'
                  value={barcodeValue}
                  onChange={(e) => setBarcodeValue(e.target.value)}
                  placeholder='123456789012'
                />
                <div className='BusinessTool-format-select'>
                  <label className='BusinessTool-label-small'>Format:</label>
                  <select
                    className='BusinessTool-select'
                    value={barcodeFormat}
                    onChange={(e) => setBarcodeFormat(e.target.value)}
                  >
                    <option value='CODE128'>CODE128 (General)</option>
                    <option value='EAN13'>EAN13 (Products)</option>
                    <option value='UPC'>UPC (Products)</option>
                    <option value='CODE39'>CODE39 (Inventory)</option>
                  </select>
                </div>
              </div>

              {barcodeValue && (
                <div className='BusinessTool-output-section'>
                  <div className='BusinessTool-preview Barcode-display'>
                    <Barcode 
                      value={barcodeValue}
                      format={barcodeFormat}
                      width={2}
                      height={80}
                      displayValue={true}
                    />
                  </div>
                  <button 
                    className='BusinessTool-download-button'
                    onClick={downloadBarcode}
                  >
                    Download Barcode
                  </button>
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Signature Maker */}
        <Accordion className='ToolBox-accordion'>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='ToolBox-accordion-summary'
          >
            <p className='ToolBox-accordion-title'>Signature Maker</p>
          </AccordionSummary>
          <AccordionDetails className='ToolBox-accordion-details'>
            <div className='BusinessTool-container'>
              <div className='BusinessTool-signature-section'>
                <label className='BusinessTool-label'>Draw Your Signature:</label>
                <div className='BusinessTool-signature-canvas-wrapper'>
                  <SignatureCanvas
                    ref={(ref) => setSignatureRef(ref)}
                    canvasProps={{
                      width: 600,
                      height: 300,
                      className: 'BusinessTool-signature-canvas'
                    }}
                  />
                </div>
                <div className='BusinessTool-signature-buttons'>
                  <button 
                    className='BusinessTool-clear-button'
                    onClick={clearSignature}
                  >
                    Clear
                  </button>
                  <button 
                    className='BusinessTool-download-button'
                    onClick={downloadSignature}
                  >
                    Download Signature
                  </button>
                </div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}

export default ToolBox;